/**
 * BuyTokensModal
 *
 * Modal for purchasing token packages via cryptocurrency.
 * Displays available packages with pricing and crypto payment options.
 */

import { useReducer, useEffect, useCallback } from 'react';
import {
  X,
  Package,
  Check,
  Sparkles,
  Loader2,
  Copy,
  CheckCircle,
  ArrowLeft,
  ExternalLink,
  Bitcoin,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { PromoCodeInput } from '../promo';
import { handleError } from '../../utils/errorHandler';
import { TIMEOUTS } from '../../constants/timings';
import { fetchPackages as apiFetchPackages, createCheckout } from '../../services/paymentApi';
import {
  buyTokensReducer,
  initialState,
  type TokenPackage,
  type PaymentDetails,
  type CurrencyOption,
} from './buyTokensReducer';

// ============================================================================
// Types
// ============================================================================

interface BuyTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// SVG crypto icons for supported chains
function CryptoIcon({ code, size = 16 }: { code: string; size?: number }) {
  switch (code) {
    case 'ETH':
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#627EEA" />
          <path d="M16.498 4v8.87l7.497 3.35L16.498 4z" fill="#fff" fillOpacity={0.6} />
          <path d="M16.498 4L9 16.22l7.498-3.35V4z" fill="#fff" />
          <path d="M16.498 21.968v6.027L24 17.616l-7.502 4.352z" fill="#fff" fillOpacity={0.6} />
          <path d="M16.498 27.995v-6.028L9 17.616l7.498 10.379z" fill="#fff" />
          <path d="M16.498 20.573l7.497-4.353-7.497-3.348v7.701z" fill="#fff" fillOpacity={0.2} />
          <path d="M9 16.22l7.498 4.353v-7.701L9 16.22z" fill="#fff" fillOpacity={0.6} />
        </svg>
      );
    case 'BNB':
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#F3BA2F" />
          <path
            d="M12.116 14.404L16 10.52l3.886 3.886 2.26-2.26L16 6l-6.144 6.144 2.26 2.26zM6 16l2.26-2.26L10.52 16l-2.26 2.26L6 16zm6.116 1.596L16 21.48l3.886-3.886 2.26 2.259L16 26l-6.144-6.144-.003-.003 2.263-2.257zM21.48 16l2.26-2.26L26 16l-2.26 2.26L21.48 16zm-2.014-.002h.002L16 12.532l-2.874 2.874-.036.036-.406.408-.15.15.592.592L16 19.468l3.468-3.468.001-.001-.003-.001z"
            fill="#fff"
          />
        </svg>
      );
    case 'SOL':
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#000" />
          <defs>
            <linearGradient id="sol-grad" x1="8" y1="24" x2="24" y2="8">
              <stop stopColor="#9945FF" />
              <stop offset="1" stopColor="#14F195" />
            </linearGradient>
          </defs>
          <path
            d="M10.34 20.22a.54.54 0 01.38-.16h13.1a.27.27 0 01.19.46l-2.73 2.73a.54.54 0 01-.38.16H7.8a.27.27 0 01-.19-.46l2.73-2.73zM10.34 8.71a.55.55 0 01.38-.16h13.1a.27.27 0 01.19.46l-2.73 2.73a.54.54 0 01-.38.16H7.8a.27.27 0 01-.19-.46l2.73-2.73zM21.66 14.42a.54.54 0 00-.38-.16H8.18a.27.27 0 00-.19.46l2.73 2.73a.54.54 0 00.38.16h13.1a.27.27 0 00.19-.46l-2.73-2.73z"
            fill="url(#sol-grad)"
          />
        </svg>
      );
    case 'TRX':
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#EF0027" />
          <path
            d="M21.932 9.36L7.784 7.063l8.416 19.078.1-.14 5.768-8.17-.004-.002.102-.16-2.02-1.478 1.786-6.83zm-1.673 1.073l-1.282 4.905-3.925-3.206 5.207-1.699zm-6.17 2.14l4.603 3.76-6.46-1.263 1.857-2.497zm4.914 4.512l-5.508 7.804L8.952 8.896l10.043 8.19zm.516-.498l1.49 1.09-3.922 5.556 2.432-6.646z"
            fill="#fff"
          />
        </svg>
      );
    case 'BTC':
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#F7931A" />
          <path
            d="M22.5 14.06c.31-2.08-1.27-3.2-3.44-3.95l.7-2.82-1.72-.43-.69 2.75c-.45-.11-.91-.22-1.38-.32l.69-2.77-1.72-.43-.7 2.82c-.37-.09-.74-.17-1.1-.26l-2.37-.59-.46 1.84s1.27.29 1.25.31c.7.17.82.63.8 1l-.8 3.22c.05.01.11.03.18.06l-.18-.05-1.12 4.51c-.09.21-.3.53-.78.41.02.02-1.25-.31-1.25-.31l-.85 1.97 2.24.56c.42.1.83.21 1.23.31l-.71 2.86 1.72.43.7-2.83c.47.13.93.25 1.38.36l-.7 2.81 1.72.43.71-2.85c2.92.55 5.12.33 6.04-2.31.75-2.13-.04-3.35-1.57-4.15 1.12-.26 1.96-1 2.18-2.52zm-3.9 5.47c-.53 2.13-4.11.98-5.27.69l.94-3.77c1.16.29 4.88.86 4.33 3.08zm.53-5.5c-.48 1.94-3.46.95-4.43.71l.85-3.42c.97.24 4.09.7 3.58 2.71z"
            fill="#fff"
          />
        </svg>
      );
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#8B5CF6" />
          <text x="16" y="21" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold">
            $
          </text>
        </svg>
      );
  }
}

// ============================================================================
// Component
// ============================================================================

export function BuyTokensModal({ isOpen, onClose }: BuyTokensModalProps) {
  const { t } = useTranslation();
  const { user, getIdToken } = useAuth();
  const [state, dispatch] = useReducer(buyTokensReducer, initialState);

  const {
    packages,
    supportedCurrencies,
    loading,
    purchasing,
    error,
    selectedCurrency,
    paymentDetails,
    copied,
    promoCode,
  } = state;

  // Fetch available packages and supported currencies
  const fetchPackages = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });

    try {
      const result = await apiFetchPackages();
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: {
          packages: result.packages as TokenPackage[],
          currencies: result.currencies as CurrencyOption[],
        },
      });
    } catch (err) {
      dispatch({
        type: 'FETCH_ERROR',
        payload: handleError(err, 'BuyTokensModal', 'Failed to load packages'),
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      dispatch({ type: 'MODAL_OPENED' });
    }
  }, [isOpen, fetchPackages]);

  // Handle package purchase
  const handlePurchase = async (packageId: string) => {
    if (!user) {
      dispatch({ type: 'SET_ERROR', payload: t('buyTokensModal.signInToPurchase') });
      return;
    }

    dispatch({ type: 'SET_PURCHASING', payload: packageId });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const details = await createCheckout({
        packageId,
        authToken: token,
        successUrl: `${window.location.origin}/tokens/success`,
        cancelUrl: `${window.location.origin}/tokens/cancelled`,
        currency: selectedCurrency,
        promoCode,
      });

      // Show payment details instead of redirecting
      dispatch({ type: 'SET_PAYMENT_DETAILS', payload: details as PaymentDetails });
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        payload: handleError(err, 'BuyTokensModal', 'Failed to create payment'),
      });
    } finally {
      dispatch({ type: 'SET_PURCHASING', payload: null });
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, type: 'address' | 'amount') => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch({ type: 'SET_COPIED', payload: type });
      setTimeout(() => dispatch({ type: 'SET_COPIED', payload: null }), TIMEOUTS.COPY_FEEDBACK);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      dispatch({ type: 'SET_COPIED', payload: type });
      setTimeout(() => dispatch({ type: 'SET_COPIED', payload: null }), TIMEOUTS.COPY_FEEDBACK);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Go back to package selection
  const handleBack = () => {
    dispatch({ type: 'BACK_TO_PACKAGES' });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-secondary-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {paymentDetails && (
              <button
                onClick={handleBack}
                className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-secondary-500" />
              </button>
            )}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white">
                {paymentDetails
                  ? t('buyTokensModal.completePayment')
                  : t('buyTokensModal.buyTokens')}
              </h2>
              <p className="text-secondary-500 dark:text-secondary-400 mt-1 text-sm">
                {paymentDetails
                  ? t('buyTokensModal.sendCryptoSubtitle')
                  : t('buyTokensModal.payWithCryptoSubtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t('buyTokensModal.closeModal')}
          >
            <X className="h-5 w-5 text-secondary-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {paymentDetails ? (
            // Payment Details View
            <PaymentDetailsView details={paymentDetails} copied={copied} onCopy={copyToClipboard} />
          ) : loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={fetchPackages}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
              >
                {t('common.tryAgain')}
              </button>
            </div>
          ) : (
            <>
              {/* Crypto Coming Soon Banner */}
              {packages.some((p) => p.available === false) && (
                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-3">
                    <Bitcoin className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                        {t('buyTokensModal.cryptoComingSoonTitle')}
                      </h3>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        {t('buyTokensModal.cryptoComingSoonBody')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Currency Selector */}
              {supportedCurrencies.length > 0 && (
                <div className="mb-6">
                  <label className="label-themed">{t('buyTokensModal.payWith')}</label>
                  <div className="flex flex-wrap gap-2">
                    {supportedCurrencies.map((crypto) => (
                      <button
                        key={crypto.code}
                        onClick={() =>
                          dispatch({ type: 'SET_SELECTED_CURRENCY', payload: crypto.code })
                        }
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                          selectedCurrency === crypto.code
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                        }`}
                      >
                        <CryptoIcon code={crypto.code} size={18} />
                        <span>{crypto.code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Promo Code Input */}
              <div className="mb-6">
                <PromoCodeInput
                  onApply={(discount, code) => {
                    dispatch({ type: 'SET_PROMO_DISCOUNT', payload: discount });
                    dispatch({ type: 'SET_PROMO_CODE', payload: code });
                  }}
                  disabled={purchasing !== null}
                />
              </div>

              {/* Packages Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all flex flex-col ${
                      pkg.popular
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-600'
                    }`}
                  >
                    {/* Popular badge */}
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                        {t('buyTokensModal.bestValue')}
                      </div>
                    )}

                    {/* Package header */}
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="h-5 w-5 text-primary-500" />
                      <h3 className="font-semibold text-secondary-900 dark:text-white">
                        {pkg.name}
                      </h3>
                    </div>

                    {/* Price */}
                    <div className="mb-4">
                      <span className="text-2xl lg:text-3xl font-bold text-secondary-900 dark:text-white whitespace-nowrap">
                        ${pkg.price_usd.toFixed(2)}
                      </span>
                    </div>

                    {/* Features */}
                    <ul className="space-y-2 mb-4 flex-1">
                      <li className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-400">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>
                          {t('paymentDetails.tokensCount', { count: pkg.tokens.toLocaleString() })}
                        </span>
                      </li>
                      {pkg.discount_percent > 0 && (
                        <li className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
                          <Sparkles className="h-4 w-4 flex-shrink-0 self-start mt-0.5" />
                          <span className="flex flex-col leading-tight">
                            <span>
                              {t('buyTokensModal.percentOff', { percent: pkg.discount_percent })}
                            </span>
                            <span>
                              {t('buyTokensModal.save', { amount: pkg.savings_usd.toFixed(2) })}
                            </span>
                          </span>
                        </li>
                      )}
                      <li className="text-xs text-secondary-500 dark:text-secondary-500">
                        {t('buyTokensModal.pricePer1000', { price: pkg.price_per_1000 })}
                      </li>
                    </ul>

                    {/* Buy button */}
                    <button
                      onClick={() => pkg.available !== false && handlePurchase(pkg.id)}
                      disabled={purchasing !== null || pkg.available === false}
                      className={`w-full py-2.5 rounded-lg font-medium transition-all min-h-[44px] mt-auto ${
                        pkg.available === false
                          ? 'bg-secondary-100 dark:bg-secondary-800 cursor-not-allowed text-secondary-400'
                          : purchasing === pkg.id
                            ? 'bg-secondary-200 dark:bg-secondary-700 cursor-wait text-secondary-500'
                            : pkg.popular
                              ? 'bg-primary-500 hover:bg-primary-600 text-white'
                              : 'bg-secondary-100 dark:bg-secondary-700 hover:bg-secondary-200 dark:hover:bg-secondary-600 text-secondary-900 dark:text-white'
                      }`}
                    >
                      {pkg.available === false ? (
                        <span className="flex items-center justify-center gap-2">
                          <Bitcoin className="h-4 w-4" />
                          {t('buyTokensModal.cryptoSoon')}
                        </span>
                      ) : purchasing === pkg.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('buyTokensModal.creating')}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <CryptoIcon code={selectedCurrency} size={18} />
                          {t('buyTokensModal.payWithCurrency', { currency: selectedCurrency })}
                        </span>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Info text */}
              <p className="text-center text-xs text-secondary-500 dark:text-secondary-400 mt-6">
                {t('buyTokensModal.secureInfo')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Payment Details Sub-component
// ============================================================================

interface PaymentDetailsViewProps {
  details: PaymentDetails;
  copied: 'address' | 'amount' | null;
  onCopy: (text: string, type: 'address' | 'amount') => void;
}

function PaymentDetailsView({ details, copied, onCopy }: PaymentDetailsViewProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      {/* Package Info */}
      <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-primary-600 dark:text-primary-400">
              {t('paymentDetails.packageLabel')}
            </p>
            <p className="text-lg font-bold text-primary-900 dark:text-primary-100">
              {details.package_name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-primary-600 dark:text-primary-400">
              {t('paymentDetails.youGet')}
            </p>
            <p className="text-lg font-bold text-primary-900 dark:text-primary-100">
              {t('paymentDetails.tokensCount', { count: details.package_tokens.toLocaleString() })}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Instructions */}
      <div className="space-y-4">
        <div className="flex-row-muted">
          <div className="badge-step">1</div>
          <span className="font-medium">{t('paymentDetails.sendExactly')}</span>
        </div>

        <div className="p-4 bg-secondary-100 dark:bg-secondary-700 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                {details.payment_amount} {details.payment_currency}
              </p>
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                ≈ ${details.price_usd.toFixed(2)} USD
              </p>
            </div>
            <button
              onClick={() => onCopy(details.payment_amount, 'amount')}
              className="p-3 rounded-lg bg-white dark:bg-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-500 transition-colors"
            >
              {copied === 'amount' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <Copy className="h-5 w-5 text-secondary-500" />
              )}
            </button>
          </div>
        </div>

        <div className="flex-row-muted">
          <div className="badge-step">2</div>
          <span className="font-medium">
            {t('paymentDetails.toAddress', { chain: details.chain })}
          </span>
        </div>

        <div className="p-4 bg-secondary-100 dark:bg-secondary-700 rounded-xl">
          <div className="flex items-center gap-3">
            <code className="flex-1 text-sm font-mono text-secondary-900 dark:text-white break-all">
              {details.payment_address}
            </code>
            <button
              onClick={() => onCopy(details.payment_address, 'address')}
              className="p-3 rounded-lg bg-white dark:bg-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-500 transition-colors flex-shrink-0"
            >
              {copied === 'address' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <Copy className="h-5 w-5 text-secondary-500" />
              )}
            </button>
          </div>
        </div>

        <div className="flex-row-muted">
          <div className="badge-step">3</div>
          <span className="font-medium">{t('paymentDetails.waitConfirmation')}</span>
        </div>

        <p className="text-sm text-secondary-500 dark:text-secondary-400 pl-8">
          {t('paymentDetails.creditedAutoMinutes')}
        </p>
      </div>

      {/* Warning */}
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          <strong>{t('paymentDetails.importantLabel')}</strong>{' '}
          {t('paymentDetails.warningText', {
            currency: details.payment_currency,
            chain: details.chain,
          })}
        </p>
      </div>

      {/* Explorer Link */}
      <a
        href={`https://etherscan.io/address/${details.payment_address}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
      >
        <ExternalLink className="h-4 w-4" />
        {t('paymentDetails.viewExplorer')}
      </a>
    </div>
  );
}
